import { Relations } from "@inrev/common";
import {
	useDeleteAdminNoteTopic,
	useFetchAdminNoteTopics,
	useUpdateAdminNote,
} from "@portal/api/note/admin";
import { AdminNote, AdminNoteTopic } from "@portal/api/note/admin/types";
import { Spinner } from "@portal/components/ui/Spinner";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ToolContextMenu } from "../shared/components/ToolContextMenu";
import { AdminNotesDefaultView } from "./AdminNotesDefaultView";
import { AdminNotesTopicView } from "./AdminNotesTopicView";
import { AdminNoteComponent } from "./components/AdminNote";
import { ArchiveMenuItem, PinMenuItem } from "./components/AdminNoteMenuItems";

export const AdminNotesTool = ({ relation }: { relation: Relations.Notes }) => {
	const { deleteAdminNoteTopic } = useDeleteAdminNoteTopic();
	const { updateAdminNote } = useUpdateAdminNote();
	const [searchParams, setSearchParams] = useSearchParams();
	const { defaultTopic, topics, defaultArchivedTopic, archivedTopics, adminNoteTopicsIsLoading } =
		useFetchAdminNoteTopics(relation);

	const [currentTopicId, setCurrentTopicId] = useState<string | undefined>(
		() => searchParams.get("noteTopicId") ?? undefined,
	);

	useEffect(() => {
		setCurrentTopicId(searchParams.get("noteTopicId") ?? undefined);
	}, [searchParams]);

	const currentTopic = useMemo(
		() => topics?.find((topic) => topic.id === currentTopicId),
		[currentTopicId, topics],
	);

	const setCurrentTopic = (topic?: AdminNoteTopic) => {
		setCurrentTopicId(topic?.id);
		if (topic === undefined) {
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				next.delete("noteTopicId");
				return next;
			});
		} else {
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				next.set("noteTopicId", topic.id);
				return next;
			});
		}
	};

	const deleteOrArchiveTopic = () => {
		if (currentTopic !== undefined) {
			deleteAdminNoteTopic({ noteTopicId: currentTopic.id, relation });
			setCurrentTopic(undefined);
		}
	};

	const archiveNote = (note: AdminNote) => {
		updateAdminNote({
			data: {
				isArchived: !note.isArchived,
			},
			noteId: note.id,
			topicId: note.topicId,
			relation,
		});
	};

	const pinNote = (note: AdminNote) => {
		updateAdminNote({
			data: {
				isPinned: !note.isPinned,
			},
			noteId: note.id,
			topicId: note.topicId,
			relation,
		});
	};

	const currentNotes = useMemo(() => {
		const notes = currentTopic?.notes ?? defaultTopic?.notes;
		const topic = currentTopic ?? defaultTopic;
		if (!notes || !topic || !topics) return null;
		return (
			<div className="flex flex-col gap-5">
				{notes.map((note, idx) => {
					return (
						<AdminNoteComponent
							key={idx}
							note={note}
							relation={relation}
							topicId={topic.id}
							topics={topics}
							setCurrentTopic={setCurrentTopic}
							isUpdateAllowed={true}
							noteMenu={
								<ToolContextMenu>
									<PinMenuItem
										isPinned={note.isPinned}
										pinNote={() => pinNote(note)}
										disabled={false}
										key={`pin-${note.id}`}
									/>
									<ArchiveMenuItem
										isArchived={note.isArchived}
										archiveNote={() => archiveNote(note)}
										disabled={false}
										key={`archive-${note.id}`}
									/>
								</ToolContextMenu>
							}
						/>
					);
				})}
			</div>
		);
	}, [defaultTopic, currentTopic, topics]);

	if (defaultTopic === undefined || topics === undefined || adminNoteTopicsIsLoading) {
		return <Spinner />;
	}

	if (currentTopic === undefined) {
		return (
			<AdminNotesDefaultView
				relation={relation}
				defaultTopic={defaultTopic}
				topics={topics}
				defaultArchivedTopic={defaultArchivedTopic}
				archivedTopics={archivedTopics}
				setCurrentTopic={setCurrentTopic}
				notesList={currentNotes}
			/>
		);
	}

	const currentTopicDeletable = currentTopic.notes?.length === 0 && currentTopic.summary === "";

	return (
		<AdminNotesTopicView
			relation={relation}
			topic={currentTopic}
			onBack={() => setCurrentTopic(undefined)}
			onDelete={currentTopicDeletable ? deleteOrArchiveTopic : undefined}
			onArchive={currentTopicDeletable ? undefined : deleteOrArchiveTopic}
			notesList={currentNotes}
		/>
	);
};
